/**
 * Direct Mail — web canvassing map.
 *
 * A rep pans satellite imagery and clicks rooftops to pin them for a mailer.
 * Each click drops a marker immediately and resolves the street address in the
 * background, because waiting on a geocode round trip before showing anything
 * makes fast pinning feel broken.
 *
 * Houses the org may not mail (do-not-knock, already pinned, existing customer)
 * are shown as suppressed markers rather than silently disappearing — a pin
 * that vanishes reads as a bug, and the rep needs to know why the house is out.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/joy';
import type { ColorPaletteProp } from '@mui/joy';
import {
  ArrowsClockwise as RetryIcon,
  Crosshair as LocateIcon,
  ListChecks as QueueIcon,
  Plus as AddIcon,
  Prohibit as SkipIcon,
  Trash as RemoveIcon,
  X as DismissIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { AddressAutocompleteInput } from '../../components/common/AddressAutocompleteInput';
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable';
import {
  DIRECT_MAIL_ROUTES,
  DirectMailPageHeader,
} from '../../components/directmail/DirectMailNav';
import { SkipPinModal } from '../../components/directmail/SkipPinModal';
import { PinMap, type PinMapMarker, type PinMapTarget } from '../../components/directmail/PinMap';
import {
  SendPinsToQueueModal,
  type SendPinsToQueueChoice,
} from '../../components/directmail/SendPinsToQueueModal';
import { loadAllPages } from '../../components/directmail/loadAllPages';
import {
  createReverseGeocodeQueue,
  forwardGeocode,
  type GeocodedAddress,
} from '../../components/directmail/pinGeocoding';
import {
  DRAFT_PIN_COLOR,
  PIN_STATUS_COLORS,
  SUPPRESSED_PIN_COLOR,
} from '../../components/directmail/pinMarkers';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  apiErrorStatus,
  formatMoney,
  formatQuoteRange,
  mailCampaignsApi,
  mailMeasurementOrdersApi,
  mailRatesApi,
  mailerPinsApi,
  moneyToNumber,
  pinPointLatLng,
} from '../../lib/directMailApi';
import type { Organization, ParsedAddress } from '../../types';
import type { MailerPin, MailerPinStatus, SuppressionReason } from '../../types/directMail';
import {
  MAILER_PIN_STATUS_LABELS,
  SUPPRESSION_REASON_LABELS,
} from '../../types/directMail';

const CAMPAIGN_STORAGE_KEY = 'directMail.map.campaignId';
const MAP_HEIGHT = 620;
// Roughly a 4.5-mile square around the office: enough neighborhoods in view to
// choose one, while individual rooftops are still a scroll-wheel away.
const OPENING_AREA_SQ_MILES = 20;

/** Statuses whose pins are still the rep's to withdraw from the map. */
const REMOVABLE_STATUSES: MailerPinStatus[] = ['pinned', 'queued'];

/** Statuses the measurement queue actually lists — anything else has no row to open. */
const QUEUE_VISIBLE_STATUSES: MailerPinStatus[] = ['queued', 'measuring', 'measured'];

const STATUS_CHIP_COLOR: Record<MailerPinStatus, ColorPaletteProp> = {
  pinned: 'primary',
  queued: 'warning',
  measuring: 'warning',
  measured: 'primary',
  approved: 'success',
  batched: 'neutral',
  mailed: 'success',
  responded: 'success',
  skipped: 'neutral',
};

const LEGEND_STATUSES: MailerPinStatus[] = [
  'pinned',
  'queued',
  'measuring',
  'measured',
  'approved',
  'batched',
  'mailed',
  'responded',
];

/**
 * A house clicked during this browser session. It exists on the map before the
 * server knows about it, so its lifecycle is tracked separately from the pins
 * returned by map-data.
 */
type SessionEntryState = 'geocoding' | 'saving' | 'saved' | 'rejected' | 'failed';

interface SessionEntry {
  key: string;
  /** The campaign this house was clicked for — entries are hidden when it changes. */
  campaign: number;
  lat: number;
  lng: number;
  address: string;
  state: SessionEntryState;
  pinId?: number;
  status?: MailerPinStatus;
  reasons?: SuppressionReason[];
  message?: string;
}

const pinKey = (id: number) => `pin-${id}`;

function pinIdFromKey(key: string | null): number | null {
  if (!key || !key.startsWith('pin-')) return null;
  const id = Number(key.slice(4));
  return Number.isFinite(id) ? id : null;
}

function entryCaption(entry: SessionEntry): string {
  switch (entry.state) {
    case 'geocoding':
      return 'Looking up the address…';
    case 'saving':
      return 'Saving the pin…';
    case 'saved':
      return MAILER_PIN_STATUS_LABELS[entry.status ?? 'pinned'];
    case 'rejected':
      return (
        entry.reasons?.map((reason) => SUPPRESSION_REASON_LABELS[reason]).join(' · ') ||
        entry.message ||
        'Not eligible for this campaign'
      );
    case 'failed':
      return entry.message || 'Could not pin this house';
  }
}

function entryChip(entry: SessionEntry): { label: string; color: ColorPaletteProp } {
  switch (entry.state) {
    case 'geocoding':
    case 'saving':
      return { label: 'Working…', color: 'neutral' };
    case 'saved':
      return {
        label: MAILER_PIN_STATUS_LABELS[entry.status ?? 'pinned'],
        color: STATUS_CHIP_COLOR[entry.status ?? 'pinned'],
      };
    case 'rejected':
      return { label: 'Suppressed', color: 'danger' };
    case 'failed':
      return { label: 'Failed', color: 'warning' };
  }
}

export const DirectMailMapPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isOrgAdmin = Boolean(user?.is_organization_admin || user?.is_superuser);
  const geocodeQueueRef = useRef(createReverseGeocodeQueue());
  const draftCounterRef = useRef(0);
  const hasCenteredRef = useRef(false);

  const [chosenCampaignId, setCampaignId] = useState<number | null>(() => {
    const stored = window.localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });
  const [entries, setEntries] = useState<SessionEntry[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [pinningEnabled, setPinningEnabled] = useState(true);
  const [mapTarget, setMapTarget] = useState<PinMapTarget | null>(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [campaignFormOpen, setCampaignFormOpen] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [needsAddressHint, setNeedsAddressHint] = useState(false);

  // Every page, not the first: the API ignores `page_size`, and a campaign that
  // fell off page one would send the picker's fallback — and the rep's next
  // street of pins — into a campaign they never chose.
  const campaignsQuery = useQuery({
    queryKey: ['direct-mail', 'campaigns'],
    queryFn: () => loadAllPages((page) => mailCampaignsApi.list({ page })),
  });
  const campaigns = useMemo(() => campaignsQuery.data ?? [], [campaignsQuery.data]);

  // Derived rather than stored: a remembered campaign that no longer exists (or
  // belongs to another org) would otherwise pin houses into nothing.
  const campaignId = useMemo(() => {
    if (chosenCampaignId !== null && campaigns.some((campaign) => campaign.id === chosenCampaignId)) {
      return chosenCampaignId;
    }
    if (campaigns.length === 0) return null;
    return (campaigns.find((campaign) => campaign.status === 'active') ?? campaigns[0]).id;
  }, [campaigns, chosenCampaignId]);

  const mapDataQuery = useQuery({
    queryKey: ['direct-mail', 'map-data', campaignId],
    queryFn: () => mailerPinsApi.mapData({ campaign: campaignId ?? undefined }),
    enabled: campaignId !== null,
  });

  const ratesQuery = useQuery({
    queryKey: ['direct-mail', 'rates'],
    queryFn: () => mailRatesApi.list(),
    staleTime: 10 * 60 * 1000,
  });

  const organizationQuery = useQuery({
    queryKey: ['direct-mail', 'map', 'organization'],
    queryFn: async () => (await apiClient.get<Organization>('/api/organizations/current/')).data,
    staleTime: 10 * 60 * 1000,
  });

  const selectedPinId = pinIdFromKey(selectedKey);
  const selectedPinQuery = useQuery({
    queryKey: ['direct-mail', 'pin', selectedPinId],
    queryFn: () => mailerPinsApi.get(selectedPinId as number),
    enabled: selectedPinId !== null,
  });

  const sessionEntries = useMemo(
    () => entries.filter((entry) => entry.campaign === campaignId),
    [entries, campaignId]
  );

  const selectedEntry = useMemo(
    () => sessionEntries.find((entry) => entry.key === selectedKey) ?? null,
    [sessionEntries, selectedKey]
  );

  useEffect(() => {
    if (campaignId === null) return;
    window.localStorage.setItem(CAMPAIGN_STORAGE_KEY, String(campaignId));
  }, [campaignId]);

  // Opening on a blank ocean of satellite tiles is useless, so land the map
  // where the rep most likely works: the office, then the browser's location.
  useEffect(() => {
    if (hasCenteredRef.current || organizationQuery.isLoading) return;
    hasCenteredRef.current = true;

    const organization = organizationQuery.data ?? null;
    const lat = Number(organization?.latitude);
    const lng = Number(organization?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
      setMapTarget({ lat, lng, fitAreaSqMiles: OPENING_AREA_SQ_MILES });
      return;
    }

    const officeAddress = [
      organization?.main_office_address_1,
      organization?.main_office_city,
      organization?.main_office_state,
      organization?.main_office_zip,
    ]
      .filter(Boolean)
      .join(', ');

    const fallbackToBrowser = () => {
      if (!navigator.geolocation) {
        setNeedsAddressHint(true);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setMapTarget({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            fitAreaSqMiles: OPENING_AREA_SQ_MILES,
          }),
        () => setNeedsAddressHint(true),
        { timeout: 8000 }
      );
    };

    if (!officeAddress) {
      fallbackToBrowser();
      return;
    }

    void forwardGeocode(officeAddress).then((point) => {
      if (point) {
        setMapTarget({ ...point, fitAreaSqMiles: OPENING_AREA_SQ_MILES });
        return;
      }
      fallbackToBrowser();
    });
  }, [organizationQuery.isLoading, organizationQuery.data]);

  const updateEntry = useCallback((key: string, patch: Partial<SessionEntry>) => {
    setEntries((previous) =>
      previous.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry))
    );
  }, []);

  const dropPin = useCallback(
    async (key: string, lat: number, lng: number, campaign: number) => {
      let address: GeocodedAddress;
      try {
        address = await geocodeQueueRef.current.enqueue(lat, lng);
      } catch (error) {
        updateEntry(key, {
          state: 'failed',
          message: error instanceof Error ? error.message : 'Address lookup failed.',
        });
        return;
      }

      updateEntry(key, { address: address.formatted_address, state: 'saving' });

      try {
        const check = await mailerPinsApi.checkSuppression({
          campaign,
          candidates: [
            { formatted_address: address.formatted_address, latitude: lat, longitude: lng },
          ],
        });
        const result = check.results?.[0];
        if (result?.suppressed) {
          updateEntry(key, { state: 'rejected', reasons: result.reasons ?? [] });
          return;
        }
      } catch {
        // A suppression check that cannot answer must not block the rep — the
        // create call below applies the same rules server-side anyway.
      }

      try {
        const pin = await mailerPinsApi.create({
          formatted_address: address.formatted_address,
          latitude: lat,
          longitude: lng,
          campaign,
          source: 'web',
          address_line1: address.address_line1,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
          place_id: address.place_id,
        });
        updateEntry(key, {
          state: 'saved',
          pinId: pin.id,
          status: pin.status,
          address: pin.formatted_address || address.formatted_address,
        });
        setSelectedKey((current) => (current === key ? pinKey(pin.id) : current));
        void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'map-data'] });
      } catch (error) {
        const message = getApiErrorMessage(error, 'This house could not be pinned.');
        const status = apiErrorStatus(error);
        // 400/409 is the server refusing this address (duplicate, suppressed),
        // not a transport failure — retrying it would fail identically.
        updateEntry(key, {
          state: status === 400 || status === 409 ? 'rejected' : 'failed',
          message,
        });
      }
    },
    [queryClient, updateEntry]
  );

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      if (campaignId === null) {
        toast.error('Choose a campaign before pinning houses.');
        return;
      }
      draftCounterRef.current += 1;
      const key = `draft-${draftCounterRef.current}`;
      setEntries((previous) => [
        { key, campaign: campaignId, lat, lng, address: '', state: 'geocoding' },
        ...previous,
      ]);
      setSelectedKey(key);
      void dropPin(key, lat, lng, campaignId);
    },
    [campaignId, dropPin]
  );

  const handleRetryEntry = useCallback(
    (entry: SessionEntry) => {
      if (campaignId === null) return;
      updateEntry(entry.key, { state: 'geocoding', message: undefined });
      void dropPin(entry.key, entry.lat, entry.lng, campaignId);
    },
    [campaignId, dropPin, updateEntry]
  );

  const handleDismissEntry = useCallback((key: string) => {
    setEntries((previous) => previous.filter((entry) => entry.key !== key));
    setSelectedKey((current) => (current === key ? null : current));
  }, []);

  const handleFocusEntry = useCallback((entry: SessionEntry) => {
    setSelectedKey(entry.state === 'saved' && entry.pinId ? pinKey(entry.pinId) : entry.key);
    setMapTarget({ lat: entry.lat, lng: entry.lng, zoom: 19 });
  }, []);

  const createCampaignMutation = useMutation({
    mutationFn: (name: string) => mailCampaignsApi.create({ name, status: 'active' }),
    onSuccess: (campaign) => {
      toast.success(`Campaign “${campaign.name}” is ready.`);
      setNewCampaignName('');
      setCampaignFormOpen(false);
      setCampaignId(campaign.id);
      void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'campaigns'] });
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not create the campaign.')),
  });

  const markEntriesQueued = useCallback((ids: number[]) => {
    setEntries((previous) =>
      previous.map((entry) =>
        entry.pinId && ids.includes(entry.pinId)
          ? { ...entry, status: 'queued' as MailerPinStatus }
          : entry
      )
    );
    setQueueModalOpen(false);
  }, []);

  const queueMutation = useMutation({
    mutationFn: (ids: number[]) =>
      mailerPinsApi.bulkQueue({ pin_ids: ids, fulfillment: 'self', campaign: campaignId }),
    onSuccess: (response, ids) => {
      const queued = response.queued ?? response.updated ?? ids.length;
      toast.success(`${queued} ${queued === 1 ? 'pin' : 'pins'} sent to the measurement queue.`);
      markEntriesQueued(ids);
      void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'map-data'] });
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not queue these pins.')),
  });

  // Handing roofs to ApexOS is a purchase, not a flag on the pin: the order is
  // what charges the card and what our fulfillment team is paid to work.
  const orderMutation = useMutation({
    mutationFn: (ids: number[]) => mailMeasurementOrdersApi.create({ pin_ids: ids }),
    onSuccess: (order, ids) => {
      toast.success(
        `Order ${order.reference} placed — ${order.pin_count} ${
          order.pin_count === 1 ? 'roof' : 'roofs'
        } for ${formatMoney(order.total_amount)}.`
      );
      markEntriesQueued(ids);
      void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'map-data'] });
    },
    // A 402 carries the actionable "no card on file" text; show it as written.
    onError: (error) =>
      toast.error(getApiErrorMessage(error, 'That measurement order could not be placed.')),
  });

  const applyPinUpdate = useCallback(
    (pin: MailerPin) => {
      setEntries((previous) =>
        previous.map((entry) => (entry.pinId === pin.id ? { ...entry, status: pin.status } : entry))
      );
      void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'map-data'] });
      void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'pin', pin.id] });
    },
    [queryClient]
  );

  const removeMutation = useMutation({
    mutationFn: (id: number) => mailerPinsApi.remove(id),
    onSuccess: (_result, id) => {
      toast.success('Pin removed.');
      setEntries((previous) => previous.filter((entry) => entry.pinId !== id));
      setSelectedKey(null);
      void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'map-data'] });
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not remove this pin.')),
  });

  const markers = useMemo<PinMapMarker[]>(() => {
    const byKey = new Map<string, PinMapMarker>();

    (mapDataQuery.data?.pins ?? []).forEach((point) => {
      const { lat, lng } = pinPointLatLng(point);
      const statusLabel = MAILER_PIN_STATUS_LABELS[point.status];
      byKey.set(pinKey(point.id), {
        key: pinKey(point.id),
        lat,
        lng,
        kind: 'pin',
        status: point.status,
        title: point.formatted_address,
        subtitle: point.quote_price_low
          ? `${statusLabel} · ${formatMoney(point.quote_price_low, { decimals: 0 })}`
          : statusLabel,
      });
    });

    sessionEntries.forEach((entry) => {
      if (entry.state === 'saved' && entry.pinId) {
        const status = entry.status ?? 'pinned';
        byKey.set(pinKey(entry.pinId), {
          key: pinKey(entry.pinId),
          lat: entry.lat,
          lng: entry.lng,
          kind: 'pin',
          status,
          title: entry.address,
          subtitle: MAILER_PIN_STATUS_LABELS[status],
        });
        return;
      }
      byKey.set(entry.key, {
        key: entry.key,
        lat: entry.lat,
        lng: entry.lng,
        kind: entry.state === 'rejected' ? 'suppressed' : 'draft',
        title: entry.address || 'New pin',
        subtitle: entryCaption(entry),
      });
    });

    return Array.from(byKey.values());
  }, [mapDataQuery.data, sessionEntries]);

  const savedEntries = sessionEntries.filter((entry) => entry.state === 'saved');
  const rejectedCount = sessionEntries.filter((entry) => entry.state === 'rejected').length;
  const sessionQueueableIds = savedEntries
    .filter((entry) => (entry.status ?? 'pinned') === 'pinned')
    .map((entry) => entry.pinId as number);
  const campaignQueueableIds = useMemo(() => {
    const ids = new Set<number>(sessionQueueableIds);
    (mapDataQuery.data?.pins ?? []).forEach((point) => {
      if (point.status === 'pinned') ids.add(point.id);
    });
    return Array.from(ids);
  }, [mapDataQuery.data, sessionQueueableIds]);

  const measurementRate =
    ratesQuery.data?.find((rate) => rate.key === 'measurement_service') ?? null;

  const handleQueueConfirm = useCallback(
    (choice: SendPinsToQueueChoice) => {
      const ids = choice.scope === 'session' ? sessionQueueableIds : campaignQueueableIds;
      if (ids.length === 0) return;
      if (choice.fulfillment === 'apexos') {
        orderMutation.mutate(ids);
        return;
      }
      queueMutation.mutate(ids);
    },
    [campaignQueueableIds, orderMutation, queueMutation, sessionQueueableIds]
  );

  const handleAddressSelect = useCallback((parsed: ParsedAddress) => {
    if (parsed.lat === undefined || parsed.lng === undefined) return;
    setMapTarget({ lat: parsed.lat, lng: parsed.lng, zoom: 19 });
    setNeedsAddressHint(false);
  }, []);

  const sessionColumns: ResponsiveColumn<SessionEntry>[] = [
    {
      key: 'address',
      sortValue: (row) => row.address,
      header: 'Address',
      mobileVariant: 'header',
      render: (entry) => (
        <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
          {entry.address || 'Looking up the address…'}
        </Typography>
      ),
    },
    {
      key: 'status',
      sortValue: (row) => row.state,
      header: 'Status',
      width: '30%',
      render: (entry) => {
        const chip = entryChip(entry);
        return (
          <Stack spacing={0.5} alignItems={{ xs: 'flex-end', md: 'flex-start' }}>
            <Chip size="sm" variant="soft" color={chip.color}>
              {chip.label}
            </Chip>
            {(entry.state === 'rejected' || entry.state === 'failed') && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {entryCaption(entry)}
              </Typography>
            )}
          </Stack>
        );
      },
    },
  ];

  const selectedPin = selectedPinQuery.data;

  return (
    <Box>
      <DirectMailPageHeader
        title="Pin houses"
        description="Click a rooftop on the satellite map to add it to a direct mail campaign."
        actions={
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<QueueIcon />}
            onClick={() => navigate(DIRECT_MAIL_ROUTES.queue)}
          >
            Measurement queue
          </Button>
        }
      />

      {/* The glass Card style applies backdrop-filter, which makes every card
          its own stacking context. Without a z-index here the address
          suggestions lose to the map card simply for being earlier in the DOM,
          however high the dropdown's own z-index goes. */}
      <Card sx={{ mb: 2, position: 'relative', zIndex: 2 }}>
        <CardContent sx={{ overflow: 'visible' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
            <FormControl sx={{ minWidth: 220 }}>
              <FormLabel>Campaign</FormLabel>
              <Select
                value={campaignId}
                onChange={(_event, value) => setCampaignId(value)}
                placeholder={campaignsQuery.isLoading ? 'Loading campaigns…' : 'Select a campaign'}
              >
                {campaigns.map((campaign) => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ flex: 1, minWidth: 220 }}>
              <FormLabel>Jump to an address</FormLabel>
              <AddressAutocompleteInput
                value={addressSearch}
                onChange={setAddressSearch}
                onAddressSelect={handleAddressSelect}
                placeholder="Search a street or neighborhood"
              />
            </FormControl>

            <Stack direction="row" spacing={2} alignItems="flex-end">
              <FormControl>
                <FormLabel>Click to pin</FormLabel>
                <Switch
                  checked={pinningEnabled}
                  onChange={(event) => setPinningEnabled(event.target.checked)}
                  sx={{ mt: 0.5 }}
                />
              </FormControl>
              <Button
                variant="outlined"
                color="neutral"
                startDecorator={<AddIcon />}
                onClick={() => setCampaignFormOpen((open) => !open)}
              >
                New campaign
              </Button>
            </Stack>
          </Stack>

          {(campaignFormOpen || (!campaignsQuery.isLoading && campaigns.length === 0)) && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'flex-end' }}
              sx={{ mt: 2 }}
            >
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>New campaign name</FormLabel>
                <Input
                  value={newCampaignName}
                  onChange={(event) => setNewCampaignName(event.target.value)}
                  placeholder="Spring storm — Maple Ridge"
                />
              </FormControl>
              <Button
                loading={createCampaignMutation.isPending}
                disabled={newCampaignName.trim().length === 0}
                onClick={() => createCampaignMutation.mutate(newCampaignName.trim())}
              >
                Create campaign
              </Button>
            </Stack>
          )}

          {!campaignsQuery.isLoading && campaigns.length === 0 && (
            <Alert color="warning" variant="soft" sx={{ mt: 2 }}>
              Every pin belongs to a campaign. Name your first one above and pinning turns on.
            </Alert>
          )}

          {campaignsQuery.isError && (
            <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
              {getApiErrorMessage(campaignsQuery.error, 'Campaigns could not be loaded.')}
            </Alert>
          )}

          {needsAddressHint && (
            <Alert color="neutral" variant="soft" sx={{ mt: 2 }}>
              We could not work out where you are. Search an address above to jump to the
              neighborhood you are canvassing.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        {/* A Sheet, not a Card: the global glass Card style sets backdrop-filter,
            which makes the card a containing block and traps Google's
            fullscreen map inside the card instead of the viewport. */}
        <Sheet variant="outlined" sx={{ flex: 1, minWidth: 0, p: 1, width: '100%' }}>
          <PinMap
            markers={markers}
            selectedKey={selectedKey}
            target={mapTarget}
            fitKey={mapDataQuery.data && campaignId !== null ? `campaign-${campaignId}` : null}
            pinningEnabled={pinningEnabled}
            height={MAP_HEIGHT}
            onMapClick={handleMapClick}
            onMarkerClick={setSelectedKey}
          />
        </Sheet>

        <Stack spacing={2} sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
          <Card>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 1.5 }}>
                This session
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', flex: 1, textAlign: 'center' }}>
                  <Typography level="body-xs">Pinned now</Typography>
                  <Typography level="h4">{savedEntries.length}</Typography>
                </Sheet>
                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', flex: 1, textAlign: 'center' }}>
                  <Typography level="body-xs">In campaign</Typography>
                  <Typography level="h4">
                    {mapDataQuery.isLoading ? '—' : mapDataQuery.data?.count ?? 0}
                  </Typography>
                </Sheet>
                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', flex: 1, textAlign: 'center' }}>
                  <Typography level="body-xs">Suppressed</Typography>
                  <Typography level="h4">{rejectedCount}</Typography>
                </Sheet>
              </Stack>

              <Button
                fullWidth
                startDecorator={<QueueIcon />}
                disabled={campaignQueueableIds.length === 0}
                onClick={() => setQueueModalOpen(true)}
              >
                Send {sessionQueueableIds.length || campaignQueueableIds.length} to the measurement
                queue
              </Button>

              {mapDataQuery.isError && (
                <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
                  {getApiErrorMessage(mapDataQuery.error, 'Existing pins could not be loaded.')}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <ResponsiveTable<SessionEntry>
                rows={sessionEntries}
                getRowKey={(entry) => entry.key}
                columns={sessionColumns}
                rowActions={(entry) => (
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Show on the map">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleFocusEntry(entry)}
                      >
                        <LocateIcon />
                      </IconButton>
                    </Tooltip>
                    {entry.state === 'failed' && (
                      <Tooltip title="Try this house again">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="primary"
                          onClick={() => handleRetryEntry(entry)}
                        >
                          <RetryIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(entry.state === 'failed' || entry.state === 'rejected') && (
                      <Tooltip title="Clear from the map">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => handleDismissEntry(entry.key)}
                        >
                          <DismissIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
                emptyState={
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    {campaignId === null
                      ? 'Pick a campaign, then click houses on the map.'
                      : 'Click a rooftop on the map and it shows up here.'}
                  </Typography>
                }
              />
            </CardContent>
          </Card>

          {selectedEntry && selectedEntry.state !== 'saved' && (
            <Card>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>
                  {selectedEntry.address || 'New pin'}
                </Typography>
                {selectedEntry.state === 'rejected' ? (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(selectedEntry.reasons ?? []).map((reason) => (
                        <Chip key={reason} size="sm" variant="soft" color="danger">
                          {SUPPRESSION_REASON_LABELS[reason]}
                        </Chip>
                      ))}
                    </Stack>
                    <Typography level="body-sm">
                      {selectedEntry.message ||
                        'Nothing was created for this house. It stays on the map in red until you clear it.'}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="neutral"
                      startDecorator={<DismissIcon />}
                      onClick={() => handleDismissEntry(selectedEntry.key)}
                    >
                      Clear from the map
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Typography level="body-sm">{entryCaption(selectedEntry)}</Typography>
                    {selectedEntry.state === 'failed' && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="sm"
                          startDecorator={<RetryIcon />}
                          onClick={() => handleRetryEntry(selectedEntry)}
                        >
                          Try again
                        </Button>
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          onClick={() => handleDismissEntry(selectedEntry.key)}
                        >
                          Clear
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}

          {selectedPinId !== null && (
            <Card>
              <CardContent>
                {selectedPinQuery.isLoading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Loading this pin…</Typography>
                  </Stack>
                )}

                {selectedPinQuery.isError && (
                  <Alert color="danger" variant="soft">
                    {getApiErrorMessage(selectedPinQuery.error, 'This pin could not be loaded.')}
                  </Alert>
                )}

                {selectedPin && (
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography level="title-md" sx={{ wordBreak: 'break-word' }}>
                        {selectedPin.formatted_address}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        <Chip size="sm" variant="soft" color={STATUS_CHIP_COLOR[selectedPin.status]}>
                          {MAILER_PIN_STATUS_LABELS[selectedPin.status]}
                        </Chip>
                        {selectedPin.campaign_name && (
                          <Chip size="sm" variant="outlined">
                            {selectedPin.campaign_name}
                          </Chip>
                        )}
                        {selectedPin.assigned_to_name && (
                          <Chip size="sm" variant="outlined">
                            {selectedPin.assigned_to_name}
                          </Chip>
                        )}
                      </Stack>
                    </Box>

                    {selectedPin.is_measured ? (
                      <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                        <Typography level="body-xs">Quoted price</Typography>
                        <Typography level="title-md">
                          {formatQuoteRange(
                            selectedPin.quote_price_low,
                            selectedPin.quote_price_high
                          )}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                          {moneyToNumber(selectedPin.roof_area_sqft).toLocaleString('en-US', {
                            maximumFractionDigits: 0,
                          })}{' '}
                          sq ft
                          {selectedPin.predominant_pitch
                            ? ` · ${selectedPin.predominant_pitch} pitch`
                            : ''}
                        </Typography>
                      </Sheet>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        No measurement yet, so this house has no price on it.
                      </Typography>
                    )}

                    {selectedPin.skip_reason && (
                      <Alert color="neutral" variant="soft">
                        Skipped: {selectedPin.skip_reason}
                      </Alert>
                    )}

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {QUEUE_VISIBLE_STATUSES.includes(selectedPin.status) && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          onClick={() =>
                            navigate(`${DIRECT_MAIL_ROUTES.queue}?pin=${selectedPin.id}`)
                          }
                        >
                          Open in queue
                        </Button>
                      )}
                      {selectedPin.status !== 'skipped' && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="warning"
                          startDecorator={<SkipIcon />}
                          onClick={() => setSkipModalOpen(true)}
                        >
                          Skip
                        </Button>
                      )}
                      {REMOVABLE_STATUSES.includes(selectedPin.status) && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="danger"
                          startDecorator={<RemoveIcon />}
                          loading={removeMutation.isPending}
                          onClick={() => removeMutation.mutate(selectedPin.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 1.5 }}>
                Map legend
              </Typography>
              <Stack spacing={0.75}>
                {LEGEND_STATUSES.map((status) => (
                  <Stack key={status} direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: PIN_STATUS_COLORS[status],
                        border: '2px solid white',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Typography level="body-sm">{MAILER_PIN_STATUS_LABELS[status]}</Typography>
                  </Stack>
                ))}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: DRAFT_PIN_COLOR,
                      border: '2px dashed white',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Typography level="body-sm">Saving…</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: SUPPRESSED_PIN_COLOR,
                      border: '2px solid white',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Typography level="body-sm">Suppressed — not mailable</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Stack>

      <SendPinsToQueueModal
        open={queueModalOpen}
        onClose={() => setQueueModalOpen(false)}
        sessionCount={sessionQueueableIds.length}
        campaignCount={campaignQueueableIds.length}
        rate={measurementRate}
        canOrder={isOrgAdmin}
        submitting={queueMutation.isPending || orderMutation.isPending}
        onConfirm={handleQueueConfirm}
      />

      <SkipPinModal
        open={skipModalOpen}
        pin={selectedPin ?? null}
        onClose={() => setSkipModalOpen(false)}
        onSkipped={(pin) => {
          setSkipModalOpen(false);
          applyPinUpdate(pin);
        }}
      />
    </Box>
  );
};

export default DirectMailMapPage;
